/**
 * Plan FLOW copy — the strings that belong to a screen rather than to a plan.
 *
 * What a plan is called, what it costs and what it includes now comes from GET /plans, so the
 * feature list a client reads is served from the same place the server enforces limits and the
 * two cannot drift. What stays here is the copy around those cards: the summary bar, the
 * scheduled-downgrade banner, the upgrade drawer, and the two modals.
 *
 * Components import from here — no plan copy literals in JSX.
 */

import type { BillingInterval } from "@giftforward/entitlements";

/**
 * Shared CTA — the scheduled banner and the downgrade modal both offer it (PRD F1/F3). `planName`
 * is the catalogue name of the plan being kept ("Good Pro", "Good Pro Plus").
 */
const keepPlan = (planName: string) => `Keep ${planName}`;

/**
 * Summary bar (PRD F1.2). The date label renders uppercase via CSS
 * (prototype `.cs-plan-meta-label` text-transform), so "Active until"
 * displays as ACTIVE UNTIL next to RENEWS ON.
 */
export const PLAN_SUMMARY_STRINGS = {
  title: "Your current plan",
  renewsOnLabel: "RENEWS ON",
  activeUntilLabel: "Active until",
  autoRenewLabel: "Auto-renew",
  cancelSubscription: "Cancel subscription",
} as const;

/** Ochre scheduled-downgrade banner (PRD F1.2) — bold lead sentence + plain rest. */
export const PLAN_SCHEDULED_BANNER = {
  strong: (date: string, planName: string) =>
    `Your ${planName} features stay active until ${date}.`,
  rest: (date: string, planName: string) =>
    `On ${date}, your account moves to Good Starter (free), and you won't be charged again for ${planName}.`,
  keepButton: keepPlan,
} as const;

/** Tier cards (PRD F1.3–F1.4) and the upsell modal's variant of the same cards (DIV-7761). */
export const PLAN_CARD_STRINGS = {
  yourPlanBadge: "Your plan",
  currentPlan: "Current plan",
  /** `badgeLabel` is the plan's short name ("Pro", "Pro Plus"). */
  switchTo: (badgeLabel: string) => `Switch to ${badgeLabel}`,
  downgrade: "Downgrade",
  cancelDowngrade: "Cancel downgrade",
  talkToSales: "Talk to Sales",
  getPlan: (badgeLabel: string) => `Get ${badgeLabel}`,
  included: "Included",
} as const;

/** In-app plan-upgrade modal hero copy (DIV-7761 Phase 40). */
export const PLAN_MODAL_STRINGS = {
  heroPrefix: "A gift platform built for ",
  heroEmphasis: "impact",
  subtitle: "Choose the plan that fits your gifting goals.",
} as const;

/** The drawer's "What you'll unlock" list shows this many non-divider plan features (PRD F2.2). */
export const PLAN_DRAWER_UNLOCK_FEATURE_COUNT = 6;

/**
 * Billing-interval copy for checkout — the upgrade drawer and the onboarding payment and team
 * steps. The amounts themselves come from the catalogue's offers (GET /plans), never from here.
 */
export const PLAN_INTERVAL_STRINGS = {
  pillsLabel: "Billing",
  pill: { month: "Monthly", year: "Annual" },
  unit: { month: "/month", year: "/year" },
  billed: { month: "billed monthly", year: "billed annually" },
  renews: { month: "Renews monthly.", year: "Renews annually." },
  /** Beneath the plan block's price. `saving` is the formatted annual saving, if any. */
  billingLine: (interval: BillingInterval, saving: string | null) =>
    interval === "month"
      ? "Billed monthly — cancel any time"
      : saving
        ? `Billed annually — save ${saving} a year`
        : "Billed annually",
} as const;

/** Upgrade drawer (PRD F2). Mixed bold/plain runs are split into prefix/suffix parts. */
export const PLAN_DRAWER_STRINGS = {
  title: (planName: string) => `Upgrade to ${planName}`,
  unlockHeading: "What you'll unlock",
  paymentMethodLabel: "Payment method",
  // Opens the INLINE organization-card form — never a deep link to the group Payment Methods
  // panel, whose cards sit on a different Stripe Customer and cannot pay for a subscription.
  addPaymentMethod: "Add payment method",
  changeCard: "Change",
  expires: (exp: string) => `Expires ${exp}`,
  /** `badgeLabel` is the plan's short name ("Pro", "Pro Plus"). */
  orderLineName: (badgeLabel: string) => `${badgeLabel} subscription`,
  billingPeriodLabel: "Billing period",
  totalLabel: "Total due today",
  payButton: (amount: string) => `Pay ${amount}`,
  processing: "Processing…",
  poweredByPrefix: "Powered by",
  poweredByBrand: "stripe",
  successTitlePrefix: "You're on ",
  successBodyPrefix: "Your subscription is active — you now have everything in ",
  successBodySuffix: ".",
  successRenewalPrefix: "Next renewal ",
  successRenewalSuffix: ".",
  doneButton: "Done",
  receiptNote: "A receipt has been emailed to you.",
} as const;

/** Downgrade / cancel confirmation modal (PRD F3.2–F3.4). */
export const PLAN_DOWNGRADE_MODAL_STRINGS = {
  title: "Downgrade to Good Starter",
  body: (planName: string) =>
    `You've paid for ${planName} through the end of your current term, so nothing changes today.`,
  callout: (date: string, planName: string) =>
    `You'll keep every ${planName} feature until ${date}. On that date your account moves to Good Starter (free). This is the same as turning off auto-renewal — you won't be charged again.`,
  keepButton: keepPlan,
  confirmButton: "Confirm downgrade",
} as const;

/** Enterprise routing modal (PRD F5, Q7: informational, single dismiss). */
export const PLAN_ENTERPRISE_MODAL_STRINGS = {
  title: "Let's find the right plan",
  body: "Your Good Enterprise plan runs on a custom contract, so plan changes are handled by your GfG account lead. They'll walk you through your options and any contract terms.",
  dismissButton: "Got it",
} as const;

/** Cross-cutting Plan tab strings (tech design §2.3). */
export const PLAN_TAB_STRINGS = {
  impersonationTooltip: "Plan changes are disabled while impersonating",
  talkToSalesToast: "Thanks — our team will reach out about Good Enterprise",
} as const;
